import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiUrl } from "../../../ApiUrl";
import Select from "react-select";

const AdmAddInventory = ({ onClose, onSaveSuccess, editingItem }) => {
  const orgId = localStorage.getItem("orgId");
  const branchId = localStorage.getItem("branchId");
  const userId = sessionStorage.getItem("userId");

  // Form Fields State
  const [purchaseDate, setPurchaseDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [itemName, setItemName] = useState("");
  const [value, setValue] = useState("");
  const [quantity, setQuantity] = useState("");
  const [inventoryType, setInventoryType] = useState(null);
  const [inventoryLocation, setInventoryLocation] = useState(null);
  const [statusVal, setStatusVal] = useState(null);
  const [remarks, setRemarks] = useState("");

  const [categories, setCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dropdown Lists Options
  const typeOptions = [
    { value: "Consumable", label: "Consumable" },
    { value: "Non-Consumable", label: "Non-Consumable" },
    { value: "Asset", label: "Asset" },
  ];

  const locationOptions = [
    { value: "Medical", label: "Medical" },
    { value: "Hostel", label: "Hostel" },
    { value: "Nursing College", label: "Nursing College" },
  ];

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
    { value: "Lost", label: "Lost" },
    { value: "Damaged", label: "Damaged" },
    { value: "Others", label: "Others" },
  ];

  // Fetch Category List
  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}INVENTORY/categories/?org_id=${orgId}&branch_id=${branchId}`
      );
      const data = await response.json();
      if (response.ok) {
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch Sub-Category List
  const fetchSubCategories = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}INVENTORY/subcategories/?org_id=${orgId}&branch_id=${branchId}`
      );
      const data = await response.json();
      if (response.ok) {
        setAllSubCategories(data);
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, []);

  // Initialize fields if editing
  useEffect(() => {
    if (editingItem && categories.length > 0 && allSubCategories.length > 0) {
      setPurchaseDate(editingItem.purchase_date);
      setItemName(editingItem.item_name);
      setValue(editingItem.value);
      setQuantity(editingItem.quantity);
      setRemarks(editingItem.remarks || "");

      // Find matching dropdown options
      const cat = categories.find((c) => c.category_id === editingItem.category);
      if (cat) {
        setSelectedCategory({ value: cat.category_id, label: cat.category_name });
      }

      const sub = allSubCategories.find(
        (s) => s.subcategory_id === editingItem.subcategory
      );
      if (sub) {
        setSelectedSubCategory({
          value: sub.subcategory_id,
          label: sub.subcategory_name,
        });
      }

      const typeOpt = typeOptions.find((t) => t.value === editingItem.inventory_type);
      if (typeOpt) setInventoryType(typeOpt);

      const locOpt = locationOptions.find((l) => l.value === editingItem.inventory_location);
      if (locOpt) setInventoryLocation(locOpt);

      const statOpt = statusOptions.find((s) => s.value === editingItem.status);
      if (statOpt) setStatusVal(statOpt);
    }
  }, [editingItem, categories, allSubCategories]);

  // Handle Save
  const handleSave = async (e) => {
    e.preventDefault();
    if (!purchaseDate) {
      alert("Please select a purchase date.");
      return;
    }
    if (!selectedCategory) {
      alert("Please select an inventory category.");
      return;
    }
    if (!selectedSubCategory) {
      alert("Please select an inventory sub-category.");
      return;
    }
    if (!itemName.trim()) {
      alert("Please enter an item name.");
      return;
    }
    if (!value || isNaN(value)) {
      alert("Please enter a valid numeric value.");
      return;
    }
    if (!quantity || isNaN(quantity) || parseInt(quantity) <= 0) {
      alert("Please enter a valid numeric quantity greater than 0.");
      return;
    }
    if (!inventoryType) {
      alert("Please select an inventory type.");
      return;
    }
    if (!inventoryLocation) {
      alert("Please select an inventory location.");
      return;
    }
    if (!statusVal) {
      alert("Please select a status.");
      return;
    }

    const payload = {
      purchase_date: purchaseDate,
      category: selectedCategory.value,
      subcategory: selectedSubCategory.value,
      item_name: itemName.trim(),
      value: parseFloat(value),
      quantity: parseInt(quantity),
      inventory_type: inventoryType.value,
      inventory_location: inventoryLocation.value,
      status: statusVal.value,
      remarks: remarks.trim(),
      orgId: orgId,
      branchId: branchId,
      created_by: userId || 1,
      is_active: true,
    };

    setLoading(true);
    try {
      const url = editingItem
        ? `${ApiUrl.apiurl}INVENTORY/items/${editingItem.item_id}/`
        : `${ApiUrl.apiurl}INVENTORY/items/`;
      const method = editingItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok) {
        alert(`Inventory item ${editingItem ? "updated" : "saved"} successfully!`);
        onSaveSuccess();
      } else {
        alert("Error saving item: " + JSON.stringify(result));
      }
    } catch (error) {
      console.error("Save item error:", error);
      alert("An error occurred while saving the inventory item.");
    } finally {
      setLoading(false);
    }
  };

  // Clear fields
  const handleClear = () => {
    if (!editingItem) {
      setPurchaseDate(new Date().toISOString().split("T")[0]);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setItemName("");
      setValue("");
      setQuantity("");
      setInventoryType(null);
      setInventoryLocation(null);
      setStatusVal(null);
      setRemarks("");
    } else {
      // Reset to original editing values
      setPurchaseDate(editingItem.purchase_date);
      setItemName(editingItem.item_name);
      setValue(editingItem.value);
      setQuantity(editingItem.quantity);
      setRemarks(editingItem.remarks || "");
    }
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat.category_id,
    label: cat.category_name,
  }));

  const filteredSubCategories = selectedCategory
    ? allSubCategories.filter((sub) => sub.category === selectedCategory.value)
    : [];

  const subCategoryOptions = filteredSubCategories.map((sub) => ({
    value: sub.subcategory_id,
    label: sub.subcategory_name,
  }));

  const pageTitleStyle = {
    marginBottom: "0px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "700",
    color: "#000000",
  };

  const actionButtonStyle = {
    width: "150px",
    height: "38px",
  };

  const formPanelStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #ccc",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
    borderRadius: "4px",
  };

  const fieldStyle = {
    height: "38px",
    fontSize: "14px",
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "38px",
      fontSize: "14px",
    }),
  };

  return (
    <div className="container-fluid ">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p
                style={{
                  marginBottom: "10px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                ADD INVENTORY
              </p>

              <div className="row mb-3 mt-3 mx-0">
                {/* Header Action Buttons */}
                <div className="row mb-3 mx-0">
                  <div className="col-12 d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={actionButtonStyle}
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {editingItem ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClear}
                      style={actionButtonStyle}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={onClose}
                      style={actionButtonStyle}
                    >
                      Close
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSave}>
                  <div className="row mt-3 mx-2">
                    <div className="col-12 custom-section-box">
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                        <div className="row flex-grow-1 mb-3 mt-3">
                          {/* Row 1 */}
                          <div className="col-12 col-md-3 ">
                            <label className="form-label">
                              Purchase Date
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className="form-control detail"
                              value={purchaseDate}
                              onChange={(e) => setPurchaseDate(e.target.value)}
                              style={fieldStyle}
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label">
                              Inventory Category
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={categoryOptions}
                              value={selectedCategory}
                              onChange={(option) => {
                                setSelectedCategory(option);
                                setSelectedSubCategory(null); // Reset subcategory when category changes
                              }}
                              placeholder="Select Category"
                              styles={selectStyles}
                              className="detail"
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Inventory Sub-Category{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={subCategoryOptions}
                              value={selectedSubCategory}
                              onChange={(option) =>
                                setSelectedSubCategory(option)
                              }
                              placeholder="Select Sub-Category"
                              isDisabled={!selectedCategory}
                              styles={selectStyles}
                              className="detail"
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Item Name <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control detail"
                              placeholder="Enter Name"
                              value={itemName}
                              onChange={(e) => setItemName(e.target.value)}
                              style={fieldStyle}
                            />
                          </div>

                          {/* Row 2 */}
                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Value <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control detail"
                              placeholder="Enter Value"
                              value={value}
                              onChange={(e) => setValue(e.target.value)}
                              style={fieldStyle}
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Quantity <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control detail"
                              placeholder="Enter Quantity"
                              value={quantity}
                              onChange={(e) => setQuantity(e.target.value)}
                              style={fieldStyle}
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label">
                              Inventory Type{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={typeOptions}
                              value={inventoryType}
                              onChange={(option) => setInventoryType(option)}
                              placeholder="Select"
                              className="detail"
                              styles={selectStyles}
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label">
                              Inventory Location
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={locationOptions}
                              value={inventoryLocation}
                              onChange={(option) =>
                                setInventoryLocation(option)
                              }
                              placeholder="Select"
                              styles={selectStyles}
                              className="detail"
                            />
                          </div>

                          {/* Row 3 */}
                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Status <span className="text-danger">*</span>
                            </label>
                            <Select
                              options={statusOptions}
                              value={statusVal}
                              onChange={(option) => setStatusVal(option)}
                              placeholder="Select"
                              styles={selectStyles}
                              className="detail"
                            />
                          </div>

                          <div className="col-12 col-md-3 mb-3">
                            <label className="form-label fw-bold">
                              Remarks
                            </label>
                            <input
                              type="text"
                              className="form-control detail"
                              placeholder="Enter Remarks"
                              value={remarks}
                              onChange={(e) => setRemarks(e.target.value)}
                              style={fieldStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmAddInventory;
